/**
 * @description 增长率显示组件
 */
import React from 'react';
import classnames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import DataCompareBubble, { IDataCompareData, IDataCompareHelper } from '@intelligence/v3/components/DataCompareBubble';

import './index.scss';

/**
 * 增长率类型
 */
export type TGrowthRateType = 'up' | 'down' | 'none';

/**
 * 格式化后的增长率值
 */
export interface IFormattedGrowthRate {
  type: TGrowthRateType;
  value: string;
}

/**
 * 增长率组件 Props
 */
export interface IGrowthRateProps extends IBaseProps {
  /** 是否显示箭头图标 */
  showIcon?: boolean;
  /** 格式化后的增长率值 */
  growthRate: IFormattedGrowthRate | null;
  /** 环比气泡数据，传入后增长率区域会显示气泡 */
  dataCompareData?: IDataCompareData & IDataCompareHelper;
}

/**
 * 格式化环比值
 * @param ratioValue 环比值（小数形式，如 0.5 表示 50%）
 * @returns 格式化后的环比显示对象
 */
export const formatGrowthRate = (ratioValue: number | null | undefined): IFormattedGrowthRate => {
  if (ratioValue === null || ratioValue === undefined || Number.isNaN(ratioValue) || ratioValue === 0) {
    return { type: 'none', value: '-' };
  }

  const percentage = Math.abs(ratioValue * 100).toFixed(1);

  if (ratioValue > 0) {
    return { type: 'up', value: `${percentage}%` };
  }

  return { type: 'down', value: `${percentage}%` };
};

const GrowthRate: React.FC<IGrowthRateProps> = (props) => {
  const { style, className, showIcon = true, growthRate, dataCompareData } = props;

  if (!growthRate) {
    return null;
  }

  const { type, value } = growthRate;

  // 有增长率（上升或下降）
  if (type === 'up' || type === 'down') {
    const growthRateContent = (
      <div
        style={style}
        className={classnames('dbi-growth-rate', className, {
          'dbi-row-successnum': type === 'up',
          'dbi-row-errornum': type === 'down',
        })}
      >
        {value}
        {showIcon && (
          <SvgIcon className="dbi-growth-rate__arrow" type={type === 'up' ? 'green-up-new' : 'red-down-new'} />
        )}
      </div>
    );

    if (dataCompareData) {
      return (
        <DataCompareBubble {...dataCompareData}>
          {growthRateContent}
        </DataCompareBubble>
      );
    }

    return growthRateContent;
  }

  // 无数据或零增长
  const growthRateContent = (
    <div style={style} className={classnames('dbi-growth-rate', className, 'dbi-row-nodata')}>
      {value}
    </div>
  );

  if (dataCompareData) {
    return (
      <DataCompareBubble {...dataCompareData}>
        {growthRateContent}
      </DataCompareBubble>
    );
  }

  return growthRateContent;
};

export default GrowthRate;
